import { useState, useEffect, useCallback } from "react";
import { ShieldCheck, Server, HeartHandshake, PhoneCall, ThumbsUp, Quote } from "lucide-react";
import { useCountUp } from "@/hooks/useCountUp";
import type { AdsMode } from "./AdsModeSelector";

const badges = [
  {
    icon: ShieldCheck,
    title: "Conforme RGPD",
    description: "Consentement, suppression, portabilité",
  },
  {
    icon: Server,
    title: "Données hébergées en France",
    description: "Infrastructure & Stockage EU",
  },
  {
    icon: HeartHandshake,
    title: "Sans engagement",
    description: "Résiliable à tout moment",
  },
];

const stats = [
  { icon: PhoneCall, value: 300, suffix: "+", label: "appels traités par mois en moyenne" },
  { icon: ThumbsUp, value: 94, suffix: "%", label: "de satisfaction agences" },
];

type Testimonial = { quote: string; role: string; location: string };

const testimonialsByMode: Record<AdsMode, Testimonial[]> = {
  vente: [
    {
      quote: "Les appels du soir et du samedi ne tombaient plus dans le vide. On a récupéré des contacts qu'on aurait perdus.",
      role: "Gérant",
      location: "Bordeaux",
    },
    {
      quote: "Mes négociateurs ne perdent plus de temps à requalifier par téléphone. Tout est déjà dans la fiche.",
      role: "Responsable commercial",
      location: "Paris",
    },
    {
      quote: "Un prospect rappelé dans l'heure grâce à la fiche, c'est un mandat signé la semaine suivante.",
      role: "Directrice d'agence",
      location: "Lyon",
    },
  ],
  location: [
    {
      quote: "On reçoit les fiches avant même d'arriver au bureau le matin. Ça change l'organisation.",
      role: "Directrice d'agence",
      location: "Lyon",
    },
    {
      quote: "On ne passe plus 2h par jour à filtrer les appels locataires. L'assistant fait le tri pour nous.",
      role: "Gestionnaire locatif",
      location: "Marseille",
    },
    {
      quote: "Les candidats non solvables sont identifiés avant même qu'on les rappelle. Un gain de temps énorme.",
      role: "Responsable location",
      location: "Toulouse",
    },
  ],
};



const StatCounter = ({ value, suffix }: { value: number; suffix: string }) => {
  const { ref, count } = useCountUp({ end: value, duration: 2000 });
  return (
    <span ref={ref as React.RefObject<HTMLSpanElement>} className="text-3xl md:text-4xl font-bold text-primary">
      {count.toLocaleString("fr-FR")}{suffix}
    </span>
  );
};

const TestimonialsCarousel = ({ mode }: { mode: AdsMode }) => {
  const [active, setActive] = useState(0);
  const testimonials = testimonialsByMode[mode];

  const next = useCallback(() => {
    setActive((prev) => (prev + 1) % testimonials.length);
  }, [testimonials.length]);

  useEffect(() => {
    setActive(0);
  }, [mode]);

  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next]);

  return (
    <div className="mb-10">
      <div className="card-stripe p-6 md:p-8 relative overflow-hidden">
        <Quote className="w-8 h-8 text-primary/20 absolute top-4 left-4" />
        <div className="relative min-h-[100px] md:min-h-[80px]">
          {testimonials.map((t, i) => (
            <div
              key={`${mode}-${i}`}
              className={`transition-all duration-500 ease-in-out pl-6 ${
                i === active
                  ? "opacity-100 relative"
                  : "opacity-0 absolute inset-0 pl-6 pointer-events-none"
              }`}
            >
              <p className="text-foreground font-medium text-sm md:text-base leading-relaxed">
                "{t.quote}"
              </p>
              <p className="text-muted-foreground text-xs md:text-sm mt-3">
                — {t.role}, {t.location}
              </p>
            </div>
          ))}
        </div>
      </div>
      <div className="flex justify-center gap-2 mt-3">
        {testimonials.map((_, i) => (
          <button
            key={i}
            onClick={() => setActive(i)}
            className={`w-2 h-2 rounded-full transition-all ${
              i === active ? "bg-primary w-6" : "bg-muted-foreground/30"
            }`}
            aria-label={`Témoignage ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

const AdsSocialProof = ({ mode }: { mode: AdsMode }) => (
  <section className="section-padding bg-secondary">
    <div className="container mx-auto px-4">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-10">
          <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium">
            Confiance & sécurité
          </span>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-6 mb-10">
          {stats.map((stat, i) => (
            <div key={i} className="card-stripe flex flex-col items-center text-center p-6 gap-2">
              <div className="icon-badge mb-1">
                <stat.icon className="w-5 h-5" />
              </div>
              <StatCounter value={stat.value} suffix={stat.suffix} />
              <p className="text-sm text-muted-foreground">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Testimonials carousel */}
        <TestimonialsCarousel mode={mode} />

        {/* Trust badges */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {badges.map((badge, i) => (
            <div
              key={i}
              className="card-stripe flex flex-col items-center text-center p-6 gap-3"
            >
              <div className="icon-badge">
                <badge.icon className="w-5 h-5" />
              </div>
              <p className="font-semibold text-foreground">{badge.title}</p>
              <p className="text-sm text-muted-foreground">{badge.description}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export default AdsSocialProof;
